import React, { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router";
import {
  getPaginatedOrders,
  getPaginatedOrdersBySearch,
} from "../../actions/mobileOrdersAction";
import { getAllVendorsForSuperAdmin } from "../../actions/vendorActions";
import PaginatedOrders from "../../views/Forms/Listings/PaginatedOrders.jsx";
import { getUserFromLocalStorage } from "../../utilities/user.js";
import {
  StatusBadge,
  PaymentBadge,
  DeliveryStatusBadge,
  TicketStageBadge,
} from "../../components/OrderStatusBadges";
import VendorSelector from "../../components/VendorSelector";
import {
  getOrderVendorLabel,
  resolveOrderVendorType,
  ORDER_TABLE_SORT_COLUMNS,
  sortOrderRows,
} from "../../utilities/orderTableSorting";
import AppSelect from "../../components/ui/AppSelect";
import PaymentRecoveryDialog from "../../components/Orders/PaymentRecoveryDialog";
import ColumnFilter from "../../components/table/ColumnFilter";
import FilterPane from "../../components/table/FilterPane";
import {
  getPaymentState,
  DELIVERY_STATE_CFG,
  PAYMENT_STATE_LABEL,
} from "../../utilities/paymentStatus";
import { usePermissions } from "../../utilities/usePermissions";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

/* ── List filters ────────────────────────────────────────────────────────────
   Held in the URL, not in component state, so a filtered view survives a
   reload, can be sent to a colleague as a link, and keeps its filters when
   paging (PaginatedOrders.jsx carries every parameter but `page` forward).
   The server applies them (utils/orderListFilters.js), so they cut across
   pages rather than only across the fifty rows already loaded. */
const FILTER_KEYS = ["delivery", "payment", "method", "source", "vendorType", "ticket", "from", "to"];

const readFilters = (query) =>
  FILTER_KEYS.reduce((acc, key) => {
    const value = query.get(key);
    if (value) acc[key] = value;
    return acc;
  }, {});

/** A comma-joined URL value as the list the column checklist works with. */
const listOf = (value) => String(value || "").split(",").map((v) => v.trim()).filter(Boolean);
const labelOf = (options, value) => options.find((o) => o.value === value)?.label || value;

const PAYMENT_FILTER_OPTIONS = ["PAID", "COD", "PENDING", "FAILED", "VOID"].map((state) => ({
  value: state.toLowerCase(),
  label: PAYMENT_STATE_LABEL?.[state] || state,
}));

// The tender the order was placed with (Orders.paymentMethod), which is what
// the dashboard's COD and MoMo cards count; the payment filter above is
// whether it has been paid. The two are different questions, so both funnels
// sit on the Amount column.
const METHOD_FILTER_OPTIONS = [
  { value: "COD", label: "Cash on delivery" },
  { value: "MOMO", label: "MTN MoMo" },
  { value: "Airtel", label: "Airtel Money" },
  { value: "CARD", label: "Card" },
  { value: "Visa", label: "Visa" },
  { value: "ZAMTEL", label: "Zamtel Kwacha" },
  { value: "CASH", label: "Cash (box office)" },
];

// The sources the table itself labels, in the words it labels them with.
const SOURCE_FILTER_OPTIONS = [
  { value: "Web", label: "Web" },
  { value: "Android", label: "Android" },
  { value: "iOS", label: "iOS" },
  { value: "mobile", label: "Mobile (unlabelled)" },
  { value: "InHouse", label: "In-House" },
  { value: "CallCenter", label: "Call Centre" },
  { value: "ai_agent", label: "AI Agent" },
  { value: "pos", label: "POS" },
  { value: "whatsapp", label: "WhatsApp" },
];

const VENDOR_TYPE_FILTER_OPTIONS = ["RESTAURANT", "RETAILER", "WHOLESALER", "HEALTHCARE", "BRAND", "EVENT"].map((t) => ({
  value: t,
  label: t.charAt(0) + t.slice(1).toLowerCase(),
}));

const DELIVERY_FILTER_OPTIONS = Object.entries(DELIVERY_STATE_CFG).map(([key, cfg]) => ({ value: key, label: cfg.label }));

/* Ticket orders by their own cycle (server: utils/orderListFilters.js
   ticketStateClause). While a `ticket` filter is on, the Delivery column is
   the Tickets column: a ticket order has no delivery leg (16 Oct 2026). */
const TICKET_FILTER_OPTIONS = [
  { value: "all", label: "All ticket orders" },
  { value: "sold", label: "Paid" },
  { value: "issued", label: "Tickets issued" },
  { value: "attention", label: "Needs attention" },
  { value: "unpaid", label: "Not paid" },
];

const formatShs = (n) => `Shs ${Number(n || 0).toLocaleString()}`;

const SortableHeader = ({ columnKey, label, sortConfig, onSort, title, filter, align }) => {
  const isActive = sortConfig.key === columnKey;
  const ariaSort = isActive
    ? sortConfig.direction === "asc" ? "ascending" : "descending"
    : "none";
  // One funnel per filter the column carries (Amount carries two).
  const funnels = (Array.isArray(filter) ? filter : filter ? [filter] : []).map((f) => (
    <ColumnFilter key={f.key || f.label} label={f.label || label} options={f.options} selected={f.selected} onApply={f.onApply} />
  ));

  return (
    <th aria-sort={ariaSort} className={align === "right" ? "pot-th-right" : undefined}>
      {/* A column that can be filtered carries its funnel next to the sort,
          Power BI style, so the filter is found where the column is. */}
      {align === "right" && funnels}
      <button
        type="button"
        className={`pot-sort-button${isActive ? " is-active" : ""}`}
        onClick={() => onSort(columnKey)}
        title={title || `Sort by ${label}`}
        aria-label={`Sort by ${label}${isActive ? `, currently ${ariaSort}` : ""}`}
      >
        <span>{label}</span>
        <i
          className={`fas ${
            isActive
              ? sortConfig.direction === "asc" ? "fa-sort-up" : "fa-sort-down"
              : "fa-sort"
          } pot-sort-icon`}
          aria-hidden="true"
        />
      </button>
      {align !== "right" && funnels}
    </th>
  );
};

const PaginatedOrderTable = () => {
  const user = getUserFromLocalStorage();
  const { can, loaded } = usePermissions();
  const [recoveryOrderId, setRecoveryOrderId] = useState(null);
  const canRecover = loaded && user?.result?.userType === "LUSUKU STAFF" && can("wallet:manage") && can("orders:update");
  const needsRecovery = (order) => ["FAILED", "PENDING"].includes(getPaymentState(order));
  const { mobileOrders, isLoading, totalOrders, totalAmount } = useSelector(
    (state) => state.mobileOrders
  );
  const [paneOpen, setPaneOpen] = useState(() => {
    try { return localStorage.getItem("pot-filter-pane") !== "closed"; } catch (_) { return true; }
  });
  const togglePane = () => {
    setPaneOpen((v) => {
      try { localStorage.setItem("pot-filter-pane", v ? "closed" : "open"); } catch (_) { /* per-browser convenience only */ }
      return !v;
    });
  };
  const { allVendorsForSuperAdmin, isLoadingVendors } = useSelector(
    (state) => state.superAdminVendors
  );

  const [search, setSearch] = useState("");
  const [selectedVendorId, setSelectedVendorId] = useState(null);
  const [selectedVendorName, setSelectedVendorName] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: "order", direction: "desc" });

  // const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const query = useQuery();
  const page = query.get("page") || 1;
  const filters = readFilters(query);
  const filterKey = FILTER_KEYS.map((k) => `${k}=${filters[k] || ""}`).join("&");
  const activeFilterCount = Object.keys(filters).length;

  // Change one filter: back to page 1, everything else kept, search dropped
  // (search is a different server path and cannot combine with these).
  const setFilter = (key, value) => {
    const params = new URLSearchParams(location.search);
    const text = Array.isArray(value) ? value.join(",") : value;
    if (text) params.set(key, text);
    else params.delete(key);
    params.set("page", "1");
    params.delete("searchQuery");
    setSearch("");
    history.push(`/admin/paginatedOrdersTable?${params.toString()}`);
  };

  const clearFilters = () => {
    setSearch("");
    history.push("/admin/paginatedOrdersTable?page=1");
  };

  // One descriptor per filterable column: the header funnel and the pane
  // read the same object, so they cannot disagree about what is on.
  const columnFilters = {
    source: { key: "source", label: "Source", options: SOURCE_FILTER_OPTIONS, selected: listOf(filters.source), onApply: (v) => setFilter("source", v) },
    vendorType: { key: "vendorType", label: "Vendor type", options: VENDOR_TYPE_FILTER_OPTIONS, selected: listOf(filters.vendorType), onApply: (v) => setFilter("vendorType", v) },
    payment: { key: "payment", label: "Payment", options: PAYMENT_FILTER_OPTIONS, selected: listOf(filters.payment), onApply: (v) => setFilter("payment", v) },
    method: { key: "method", label: "Paid by", options: METHOD_FILTER_OPTIONS, selected: listOf(filters.method), onApply: (v) => setFilter("method", v) },
    delivery: { key: "delivery", label: "Delivery", options: DELIVERY_FILTER_OPTIONS, selected: listOf(filters.delivery), onApply: (v) => setFilter("delivery", v) },
    ticket: { key: "ticket", label: "Tickets", options: TICKET_FILTER_OPTIONS, selected: listOf(filters.ticket), onApply: (v) => setFilter("ticket", v) },
  };
  const ticketView = Boolean(filters.ticket);
  const paneGroups = [
    ...Object.values(columnFilters).map((f) => ({
      key: f.key,
      label: f.label,
      items: f.selected.map((v) => ({ value: v, label: labelOf(f.options, v) })),
    })),
    {
      key: "dates",
      label: "Placed",
      items: [
        filters.from && { value: "from", label: `from ${filters.from}` },
        filters.to && { value: "to", label: `to ${filters.to}` },
      ].filter(Boolean),
    },
  ];
  const removeFilterValue = (key, value) => {
    if (key === "dates") return setFilter(value, "");
    const f = columnFilters[key];
    return setFilter(key, f.selected.filter((v) => v !== value));
  };
  const pageAmount = (mobileOrders || []).reduce((sum, o) => sum + Number(o?.totalPrice || o?.itemsPrice || 0), 0);

  // Vendor filter + all-vendors fetch: any Lusuku STAFF member who can open
  // this page (orders:read) benefits from filtering by vendor, supervisors
  // included, not only the super user. The endpoint behind it is auth-gated.
  const isSuperAdmin =
    user?.result?.user_role === "SUPERUSER" ||
    (user?.result?.userType === "LUSUKU STAFF" &&
      user?.result?.user_role === "SUPERVISOR");

  // Fetch all vendors if super admin
  useEffect(() => {
    if (isSuperAdmin) {
      dispatch(getAllVendorsForSuperAdmin());
    }
  }, [dispatch, isSuperAdmin]);

  // filterKey is the filters' identity as one string, so the effect re-runs
  // exactly when a filter changes and the URL is the only state it reads.
  useEffect(() => {
    if (page) {
      dispatch(
        getPaginatedOrders(page, selectedVendorId, readFilters(new URLSearchParams(filterKey)))
      );
    }
  }, [dispatch, page, selectedVendorId, filterKey]);

  if (isLoading) {
    return (
      <Spinner
        animation="grow"
        variant="danger"
        style={{
          marginTop: "70px",
          top: "50%",
          left: "50%",
          position: "fixed",
        }}
      />
    );
  }

  const handleVendorChange = (vendorId) => {
    setSelectedVendorId(vendorId);

    // Find vendor name for display
    if (vendorId && allVendorsForSuperAdmin.length > 0) {
      const vendor = allVendorsForSuperAdmin.find(v => v._id === vendorId);
      setSelectedVendorName(vendor ? vendor.name : "");
    } else {
      setSelectedVendorName("");
    }

    // Reset to page 1 when vendor changes
    history.push("/admin/paginatedOrdersTable?page=1");
    setSearch("");
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      if (search.length > 0) {
        filterOrders();
      } else {
        dispatch(getPaginatedOrders(page, selectedVendorId, filters));
        history.replace("/admin/paginatedOrdersTable");
        setSearch("");
      }
    }
  };

  const filterOrders = () => {
    if (search.trim()) {
      dispatch(getPaginatedOrdersBySearch({ search }, selectedVendorId));
      history.push(
        `/admin/paginatedOrdersTable?searchQuery=${search || "none"}`
      );
    } else {
      dispatch(getPaginatedOrders(page, selectedVendorId, filters));
      history.replace("/admin/paginatedOrdersTable");
      setSearch("");
    }
  };

  const backToOrders = () => {
    history.push("/admin/paginatedOrdersTable");
    setSearch("");
  };

  const handleInputClear = (e) => {
    setSearch(e.target.value);

    if (e.target.value === "") {
      dispatch(getPaginatedOrders(page, selectedVendorId, filters));
      history.replace("/admin/paginatedOrdersTable");
    }
  };

  const handleOrderManage = async (order) => {
    try {
      // A ticket order's own page: reservation to gate (16 Oct 2026).
      if (order.orderKind === "TICKET") {
        history.push(`/admin/ticketOrderDetails/${order._id}`);
        return;
      }

      if (order.isPackageDelivery) {
        history.push(`/admin/package-delivery/${order._id}`);
        return;
      }

      // AI agent orders — route by vendorType stored on the first item
      if (order.orderSrc === 'ai_agent' || order.source === 'ai_agent') {
        const vendorType = order.orderItems?.[0]?.vendorType;
        if (vendorType === 'restaurant') {
          history.push(`/admin/restaurantOrderDetails/${order._id}`);
        } else if (vendorType === 'brand') {
          history.push(`/admin/brandOrderDetails/${order._id}`);
        } else if (vendorType === 'retailer') {
          history.push(`/admin/retWholesalerOrderDetails/${order._id}`);
        } else if (vendorType === 'healthcare') {
          history.push(`/admin/healthcareOrderDetails/${order._id}`);
        } else {
          history.push(`/admin/orderDetails/${order._id}`);
        }
        return;
      }

      // Brand orders, however they are keyed (isBrandOrder, a productId line,
      // a populated Brand, or a POS sale keyed on the BRAND_OWNER account):
      // the one resolver the type pill uses, so a row that says BRAND opens
      // the brand page (13 Oct 2026; SWT's POS sales opened the generic one).
      if (resolveOrderVendorType(order) === "BRAND") {
        history.push(`/admin/brandOrderDetails/${order._id}`);
        return;
      }

      // Priority 3: Check the first item to determine order type
      if (order.orderItems && order.orderItems.length > 0) {
        const firstItem = order.orderItems[0];

        // Check if vendorId is a Brand (has 'name' but no 'operationMode' or 'businessName')
        if (firstItem.vendorId && typeof firstItem.vendorId === "object") {
          if (
            firstItem.vendorId.name &&
            !firstItem.vendorId.operationMode &&
            !firstItem.vendorId.businessName
          ) {
            history.push(`/admin/brandOrderDetails/${order._id}`);
            return;
          }

          // If vendorId has operationMode, route based on that
          if (firstItem.vendorId.operationMode) {
            const operationMode = firstItem.vendorId.operationMode;

            if (operationMode === "RESTAURANT") {
              history.push(`/admin/restaurantOrderDetails/${order._id}`);
              return;
            }

            if (operationMode === "RETAILER" || operationMode === "WHOLESALER" || mobileOrders[0]?.isMultiRetailer) {
              history.push(`/admin/retWholesalerOrderDetails/${order._id}`);
              return;
            }

            if (operationMode === "HEALTHCARE") {
              history.push(`/admin/healthcareOrderDetails/${order._id}`);
              return;
            }
          }
        }
      }

      // Default fallback to generic order details
      history.push(`/admin/orderDetails/${order._id}`);
    } catch (error) {
      console.error("Error navigating:", error);
      history.push(`/admin/orderDetails/${order._id}`);
    }
  };

  const handleSort = (key) => {
    setSortConfig((current) => ({
      key,
      direction:
        current.key === key
          ? current.direction === "asc" ? "desc" : "asc"
          : key === "order" ? "desc" : "asc",
    }));
  };

  // ── Badge helpers ────────────────────────────────────────────────────────
  const OPERATION_MODE_CFG = {
    RESTAURANT:  { bg: "#fde8dc", color: "#c13a00", border: "#f9c4a8", icon: "fa-utensils" },
    HEALTHCARE:  { bg: "#fde8ef", color: "#b01246", border: "#f9b8ce", icon: "fa-heartbeat" },
    RETAILER:    { bg: "#e0f2fe", color: "#035270", border: "#b3e0f7", icon: "fa-shopping-bag" },
    WHOLESALER:  { bg: "#d1fae5", color: "#065f46", border: "#a7f3d0", icon: "fa-boxes" },
    // BRAND and EVENT are in the orderItems[].vendorType enum but were absent
    // here, so those orders rendered with no type badge at all.
    BRAND:       { bg: "#ede9fe", color: "#4c1d95", border: "#ddd6fe", icon: "fa-tag" },
    EVENT:       { bg: "#fef3c7", color: "#92400e", border: "#fde68a", icon: "fa-ticket-alt" },
  };

  // Where an order came from. Everything that was not `ai_agent` previously got
  // one blue "mobile" badge, so a staff-placed in-house order and a customer's
  // phone order looked identical.
  const SOURCE_CFG = {
    ai_agent:   { label: "AI Agent",   bg: "#f0fdf4", color: "#166534", border: "#bbf7d0", icon: "fa-robot" },
    InHouse:    { label: "In-House",   bg: "#fef3c7", color: "#92400e", border: "#fde68a", icon: "fa-store" },
    CallCenter: { label: "Call Centre",bg: "#fae8ff", color: "#86198f", border: "#f5d0fe", icon: "fa-headset" },
    pos:        { label: "POS",        bg: "#e0f2fe", color: "#035270", border: "#b3e0f7", icon: "fa-cash-register" },
    whatsapp:   { label: "WhatsApp",   bg: "#dcfce7", color: "#14532d", border: "#bbf7d0", icon: "fa-comment-dots" },
    web:        { label: "Web",        bg: "#e0e7ff", color: "#3730a3", border: "#c7d2fe", icon: "fa-globe" },
  };
  const MOBILE_SOURCE_CFG = { label: "Mobile", bg: "#dbeafe", color: "#1e40af", border: "#bfdbfe", icon: "fa-mobile-alt" };

  // Match case-insensitively — orderSrc is a free String on the model, so real
  // rows carry "InHouse", "CallCenter", "ai_agent" and older unset values.
  // One copy action for the order id, used by the desktop pill and the
  // mobile card so the two never drift.
  const copyOrderId = (order) => {
    navigator.clipboard.writeText(order._id);
    setCopiedId(order._id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const sourceCfgFor = (order) => {
    const raw = order?.orderSrc || order?.source || order?.orderSource;
    if (!raw) return { ...MOBILE_SOURCE_CFG, label: "Mobile" };
    const key = Object.keys(SOURCE_CFG).find(
      (k) => k.toLowerCase() === String(raw).toLowerCase()
    );
    return key ? SOURCE_CFG[key] : { ...MOBILE_SOURCE_CFG, label: String(raw) };
  };

  // Vendor label. A BRAND order's orderItems[].vendorId points at the brand
  // OWNER (a User), which usually has no businessName — so the chain below fell
  // straight through to "N/A" for every brand order. order.brandId is the truth
  // for those; item.vendorName covers rows where it was denormalised at create.
  const vendorLabelFor = getOrderVendorLabel;

  // Vendor TYPE, read off the ORDER (its lines, then the populated seller),
  // normalised to the six types the badges and filters know. The previous
  // local version read the seller's operationMode raw, so a brand owner's
  // "BRAND_OWNER" matched no badge and every SWT POS sale showed no pill
  // (13 Oct 2026). utilities/orderTableSorting.js, shared with routing below.
  const vendorTypeFor = resolveOrderVendorType;

  // Payment and delivery badges come from components/OrderStatusBadges, which
  // derives both from utilities/paymentStatus.js. Do NOT re-declare local
  // PAYMENT_CFG / DELIVERY_CFG maps here:
  //   • the old local PAYMENT_CFG read a dead `payment_status` field, which is
  //     what produced the "details says Paid, list says PENDING" bug;
  //   • the old local DELIVERY_CFG covered only 5 of the 12 delivery_status enum
  //     values and defaulted the rest to the PENDING style, so PROCESSING,
  //     DISPATCHED, IN_TRANSIT, ARRIVED, FULFILLED and NOT_APPLICABLE all
  //     rendered as though the order were pending.
  // OPERATION_MODE_CFG stays local — it is specific to this table.

  const sortedOrders = sortOrderRows(mobileOrders, sortConfig);

  return (
    <>
      <style>{`
        .pot-page { background: #f4f6f9; min-height: 100vh; padding: 24px 20px; font-family: "Segoe UI", system-ui, sans-serif; }

        /* Panel */
        .pot-panel { background: #fff; border-radius: 10px; box-shadow: 0 1px 6px rgba(0,0,0,0.07); overflow: hidden; margin-bottom: 20px; }

        /* Dark header */
        .pot-header {
          background: linear-gradient(135deg, #1a2a3a 0%, #243447 100%);
          padding: 18px 24px; display: flex; align-items: center;
          justify-content: space-between; gap: 12px; flex-wrap: wrap;
        }
        .pot-header-left { display: flex; flex-direction: column; gap: 2px; }
        .pot-header-title { color: #fff; font-size: 1.1rem; font-weight: 800; margin: 0; display: flex; align-items: center; gap: 8px; }
        .pot-header-title i { color: #5bc5f5; }
        .pot-header-sub { color: #8aaccc; font-size: 0.78rem; margin: 0; }
        .pot-vendor-chip {
          display: inline-flex; align-items: center; gap: 7px;
          background: rgba(91,197,245,0.15); color: #5bc5f5;
          border: 1px solid rgba(91,197,245,0.3);
          padding: 5px 14px; border-radius: 20px;
          font-size: 0.75rem; font-weight: 700; white-space: nowrap; margin-top: 8px;
        }

        /* Filter bar */
        .pot-filter-bar { padding: 12px 20px; background: #fafbfc; border-bottom: 1px solid #f0f2f5; }
        .pot-search-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
        .pot-search-wrap { position: relative; flex: 1; min-width: 200px; }
        .pot-search-wrap .pot-search-icon { position: absolute; left: 13px; top: 50%; transform: translateY(-50%); color: #9aacbf; font-size: 13px; pointer-events: none; }
        .pot-search-wrap input { width: 100%; padding: 8px 34px 8px 35px; border: 1.5px solid #e4e8ef; border-radius: 8px; font-size: 0.83rem; outline: none; transition: border-color 0.2s; background: #fff; }
        .pot-search-wrap input:focus { border-color: #5bc5f5; }
        .pot-search-clear { position: absolute; right: 10px; top: 50%; transform: translateY(-50%); background: none; border: none; color: #9aacbf; cursor: pointer; padding: 2px; font-size: 12px; }
        .pot-btn { height: 34px; padding: 0 16px; border-radius: 8px; font-size: 0.8rem; font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; transition: background 0.15s; white-space: nowrap; border: none; }
        .pot-btn-search { background: #117a44; color: #fff; }
        .pot-btn-search:hover { background: #0e6236; }
        .pot-btn-clear { background: #fff; color: #6b7c93; border: 1.5px solid #e4e8ef; }
        .pot-btn-clear:hover { background: #f8d7da; color: #dc3545; border-color: #f5c2c7; }

        .pot-filter-rail-wrap { display: contents; }
        /* Date range, in the filter pane. */
        .pot-pane-dates { display: flex; flex-direction: column; gap: 6px; }
        .pot-pane-dates input[type="date"] { height: 32px; border: 1.5px solid #e4e8ef; border-radius: 6px; background: #fff; color: #2d3748; padding: 0 8px; font-size: var(--lk-text-sm, 12px); width: 100%; }
        .pot-pane-dates input.is-set { border-color: #083860; color: #083860; font-weight: 700; }

        /* Totals footer: count and amount across the WHOLE filtered set, and
           for this page, pinned under the grid. */
        table.pot-table tfoot td { padding: 9px 12px; background: #f8fafc; border-top: 2px solid #e2e8f0; font-size: 0.78rem; color: #1a2a3a; font-weight: 700; white-space: nowrap; position: sticky; bottom: 0; }
        table.pot-table tfoot td:first-child { padding-left: 20px; }
        table.pot-table tfoot .pot-foot-sub { display: block; font-size: 0.68rem; color: #8a9ab0; font-weight: 600; }
        table.pot-table th.pot-th-right, table.pot-table td.pot-td-right { text-align: right; }
        table.pot-table th.pot-th-right .pot-sort-button { flex-direction: row-reverse; }

        /* Payment review column: its own header, before Actions. */
        table.pot-table td.pot-col-payment { width: 1%; white-space: nowrap; }
        /* Filled like every other button (13 Oct 2026); the column, not the
           paint, tells it apart from Manage Order. */
        .pot-review-btn { background: #083860; color: #fff; border-color: #083860; }
        .pot-review-btn:hover { background: #0a4577; color: #fff; border-color: #0a4577; }
        .pot-col-payment .pot-none { color: #c3cdd8; }

        /* Table */
        .pot-table-wrap { overflow-x: auto; }
        table.pot-table { width: 100%; border-collapse: collapse; }
        table.pot-table thead tr { background: #f4f6f9; }
        table.pot-table thead th { padding: 10px 12px; font-size: 0.67rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: #8a9ab0; border-bottom: 2px solid #e9ecef; white-space: nowrap; border-top: none; }
        table.pot-table thead th:first-child { padding-left: 20px; }
        .pot-sort-button {
          appearance: none; background: transparent; border: 0; color: inherit;
          font: inherit; font-weight: inherit; letter-spacing: inherit;
          text-transform: inherit; padding: 0; margin: 0; cursor: pointer;
          display: inline-flex; align-items: center; gap: 6px; white-space: nowrap;
        }
        .pot-sort-button:hover, .pot-sort-button:focus-visible { color: #24577d; }
        .pot-sort-button:focus-visible { outline: 2px solid #5bc5f5; outline-offset: 4px; border-radius: 2px; }
        .pot-sort-button.is-active { color: #1a496b; }
        .pot-sort-icon { width: 10px; color: #aeb9c7; font-size: 0.65rem; }
        .pot-sort-button.is-active .pot-sort-icon { color: #2c7be5; }
        table.pot-table tbody tr { border-left: 3px solid transparent; transition: background 0.12s, border-color 0.12s; }
        table.pot-table tbody tr:hover { background: #f7fbff; border-left-color: #5bc5f5; }
        /* Dense grid (Izi, 11 Oct 2026): 8px rows, one type size across every
           column, no vertical rules, amounts right-aligned. */
        table.pot-table tbody td { padding: 8px 12px; font-size: 0.8rem; color: #2d3748; border-bottom: 1px solid #f0f2f5; vertical-align: middle; line-height: 1.3; }
        table.pot-table td.pot-col-index { width: 36px; padding-right: 4px; }
        table.pot-table td.pot-col-actions { width: 1%; white-space: nowrap; }
        table.pot-table tbody td:first-child { padding-left: 20px; }
        table.pot-table tbody tr:last-child td { border-bottom: none; }

        /* Order number pill. It IS the copy-id control: clicking it copies the
           full order id, which retired the separate "ID" button that used to
           widen every row. */
        .pot-order-num { font-family: "SFMono-Regular", Consolas, monospace; font-size: 0.75rem; background: #f4f6f9; color: #1a2a3a; padding: 2px 8px; border-radius: 6px; border: 1px solid #e4e8ef; font-weight: 700; display: inline-flex; align-items: center; gap: 5px; cursor: pointer; white-space: nowrap; transition: all 0.15s; }
        .pot-order-num:hover { background: #eaf2ff; color: #2c7be5; border-color: #b3d4fa; }
        .pot-order-num i { font-size: 9px; color: #aeb9c7; }
        .pot-order-num:hover i { color: #2c7be5; }
        .pot-order-num.is-copied { background: #e6f7f3; color: #00795f; border-color: #b8e6d8; }
        .pot-order-num.is-copied i { color: #00b69b; }
        .pot-date { font-size: 0.72rem; color: #9aacbf; margin-top: 3px; }
        /* Date and source chip share one line under the pill; the source
           used to be a column of its own. */
        .pot-order-meta { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-top: 3px; }
        .pot-order-meta .pot-date { margin-top: 0; }

        /* Amount */
        .pot-amount { color: #117a44; font-weight: 800; font-size: 0.8rem; white-space: nowrap; font-variant-numeric: tabular-nums; }
        .pot-amount-sub { font-size: 0.7rem; color: #9aacbf; margin-top: 2px; }
        .pot-amount-pay { margin-top: 3px; }

        /* Action buttons */
        .pot-view-btn { height: 30px; padding: 0 12px; border-radius: 7px; border: 1.5px solid #c8d6e5; background: transparent; color: #6b7c93; font-size: 0.78rem; font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; transition: all 0.15s; white-space: nowrap; }
        .pot-view-btn.pot-manage-btn { background: #083860; color: #fff; border-color: #083860; min-height: 32px; }
        .pot-view-btn.pot-manage-btn:hover { background: #fff; color: #083860; border-color: #083860; }
        .pot-manage-btn:focus-visible { outline: 3px solid #083860; outline-offset: 3px; }
        .pot-view-btn:hover { background: #1a2a3a; color: #fff; border-color: #1a2a3a; }
        .pot-copy-btn { height: 30px; padding: 0 10px; border-radius: 7px; border: 1.5px solid #e4e8ef; background: transparent; color: #9aacbf; font-size: 0.72rem; font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; gap: 4px; transition: all 0.15s; font-family: "SFMono-Regular", Consolas, monospace; }
        .pot-copy-btn:hover { background: #eaf2ff; color: #2c7be5; border-color: #b3d4fa; }

        /* Mobile card list */
        .pot-card-list { display: none; padding: 12px; }
        .pot-mobile-card {
          background: #fff; border-radius: 10px; padding: 14px 16px;
          border: 1px solid #e9ecef; border-left: 4px solid #5bc5f5;
          margin-bottom: 10px;
        }
        .pot-mc-row { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; }
        .pot-mc-body { flex: 1; min-width: 0; }
        .pot-mc-num { font-weight: 800; font-size: 0.88rem; color: #1a2a3a; }
        .pot-mc-vendor { font-size: 0.78rem; color: #4a5568; font-weight: 600; margin: 3px 0; }
        .pot-mc-customer { font-size: 0.73rem; color: #6b7c93; }
        .pot-mc-badges { display: flex; gap: 5px; flex-wrap: wrap; margin-top: 8px; }
        .pot-mc-footer { display: flex; align-items: center; justify-content: space-between; margin-top: 10px; padding-top: 8px; border-top: 1px solid #f0f2f5; }
        .pot-mc-amount { color: #117a44; font-weight: 800; font-size: 0.9rem; }
        .pot-mc-date { font-size: 0.7rem; color: #9aacbf; }
        .pot-mobile-sort { display: none; align-items: center; gap: 8px; width: 100%; margin-top: 8px; }
        .pot-mobile-sort label { color: #6b7c93; font-size: 0.74rem; font-weight: 700; margin: 0; white-space: nowrap; }
        .pot-mobile-sort select { flex: 1; min-width: 0; height: 34px; border: 1.5px solid #e4e8ef; border-radius: 8px; background: #fff; color: #2d3748; padding: 0 9px; font-size: 0.78rem; }
        .pot-sort-direction { width: 38px; height: 34px; border-radius: 8px; border: 1.5px solid #e4e8ef; background: #fff; color: #2c7be5; cursor: pointer; }

        /* Empty state */
        .pot-empty { text-align: center; padding: 52px 20px; }
        .pot-empty-icon { font-size: 36px; margin-bottom: 12px; opacity: 0.2; color: #8a9ab0; }
        .pot-empty-msg { font-size: 0.88rem; color: #9aacbf; font-style: italic; }

        /* Responsive */
        @media (max-width: 768px) {
          .pot-page { padding: 16px 12px; }
          .pot-header { padding: 14px 16px; }
          .pot-filter-bar { padding: 10px 12px; }
          .pot-table-wrap { display: none; }
          .pot-filter-rail-wrap { display: none; }
          .pot-card-list { display: block; }
          .pot-mobile-sort { display: flex; }
        }
      `}</style>

      <div className="pot-page">
        <div className="pot-panel">

          {/* ── Dark header ── */}
          <div className="pot-header">
            <div className="pot-header-left">
              <h4 className="pot-header-title">
                <i className="fas fa-shopping-cart" />
                Orders Management
              </h4>
              <p className="pot-header-sub">View and manage all customer orders</p>
              {selectedVendorName && (
                <span className="pot-vendor-chip">
                  <i className="fas fa-filter" style={{ fontSize: 10 }} />
                  Viewing: {selectedVendorName}
                </span>
              )}
            </div>
            {isSuperAdmin && (
              <VendorSelector
                vendors={allVendorsForSuperAdmin}
                selectedVendorId={selectedVendorId}
                onChange={handleVendorChange}
              />
            )}
          </div>

          {/* ── Filter bar ── */}
          <div className="pot-filter-bar">
            <div className="pot-search-row">
              <div className="pot-search-wrap">
                <i className="fas fa-search pot-search-icon" />
                <input
                  type="search"
                  placeholder="Search by order number, customer, item…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={handleKeyPress}
                  onInput={handleInputClear}
                />
                {search && (
                  <button className="pot-search-clear" onClick={backToOrders}>
                    <i className="fas fa-times" />
                  </button>
                )}
              </div>
              <button className="pot-btn pot-btn-search" onClick={filterOrders}>
                <i className="fas fa-search" />
                Search
              </button>
              {search && (
                <button className="pot-btn pot-btn-clear" onClick={backToOrders}>
                  <i className="fas fa-times" />
                  Clear
                </button>
              )}
              <div className="pot-mobile-sort">
                <label htmlFor="pot-mobile-sort-field">Sort by</label>
                <AppSelect
                  id="pot-mobile-sort-field"
                  value={sortConfig.key}
                  onChange={(event) => handleSort(event.target.value)}
                >
                  {ORDER_TABLE_SORT_COLUMNS.map((column) => (
                    <option key={column.key} value={column.key}>{column.label}</option>
                  ))}
                </AppSelect>
                <button
                  type="button"
                  className="pot-sort-direction"
                  onClick={() => handleSort(sortConfig.key)}
                  aria-label={`Sort ${sortConfig.direction === "asc" ? "descending" : "ascending"}`}
                  title={`Sort ${sortConfig.direction === "asc" ? "descending" : "ascending"}`}
                >
                  <i className={`fas ${sortConfig.direction === "asc" ? "fa-arrow-up" : "fa-arrow-down"}`} />
                </button>
              </div>
            </div>
          </div>

          {/* ── Desktop Table, with the filter pane on its right ── */}
          <div className="lk-table-with-pane">
          <div className="pot-table-wrap lk-table-main">
            <table className="pot-table">
              <thead>
                <tr>
                  <th>#</th>
                  <SortableHeader columnKey="order" label="Order" sortConfig={sortConfig} onSort={handleSort} title="Sort by order date" filter={columnFilters.source} />
                  <SortableHeader columnKey="vendor" label="Vendor" sortConfig={sortConfig} onSort={handleSort} filter={columnFilters.vendorType} />
                  <SortableHeader columnKey="customer" label="Customer" sortConfig={sortConfig} onSort={handleSort} />
                  <SortableHeader columnKey="amount" label="Amount" sortConfig={sortConfig} onSort={handleSort} filter={[columnFilters.payment, columnFilters.method]} align="right" />
                  <SortableHeader
                    columnKey="delivery"
                    label={ticketView ? "Tickets" : "Delivery"}
                    sortConfig={sortConfig}
                    onSort={handleSort}
                    filter={ticketView ? columnFilters.ticket : columnFilters.delivery}
                  />
                  {canRecover && <th>Payment review</th>}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {!sortedOrders.length ? (
                  <tr>
                    <td colSpan={canRecover ? 8 : 7}>
                      <div className="pot-empty">
                        <div className="pot-empty-icon"><i className="fas fa-shopping-cart" /></div>
                        <div className="pot-empty-msg">No orders found.</div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  sortedOrders.map((item, index) => {
                    const opMode = vendorTypeFor(item);
                    const delStatus = (item?.delivery_status || "PENDING").toUpperCase();
                    return (
                      <tr key={item?._id}>
                        <td className="pot-col-index" style={{ color: "#9aacbf", fontWeight: 700, fontSize: "0.75rem" }}>{index + 1}</td>
                        <td>
                          <button
                            type="button"
                            className={`pot-order-num${copiedId === item._id ? " is-copied" : ""}`}
                            title="Copy the full order ID"
                            onClick={() => copyOrderId(item)}
                          >
                            {copiedId === item._id
                              ? <><i className="fas fa-check" /> Copied</>
                              : <>#{item?.momo_id?.order_number || item?._id?.slice(-8)} <i className="fas fa-copy" /></>
                            }
                          </button>
                          <div className="pot-order-meta">
                            <div className="pot-date">{moment(item?.createdAt).format("DD MMM YYYY · HH:mm")}</div>
                            <StatusBadge
                              label={sourceCfgFor(item).label}
                              cfg={sourceCfgFor(item)}
                              style={{ fontSize: "0.62rem", padding: "1px 7px" }}
                            />
                          </div>
                        </td>
                        <td>
                          <div style={{ fontWeight: 600, color: "#1a2a3a", fontSize: "0.83rem" }}>
                            {item?.isPackageDelivery ? "Package Delivery" : vendorLabelFor(item)}
                          </div>
                          {item?.isPackageDelivery && (
                            <div style={{ marginTop: 4 }}>
                              <StatusBadge label="PACKAGE" cfg={{ bg: "#ede9fe", color: "#4c1d95", border: "#ddd6fe", icon: "fa-box" }} />
                            </div>
                          )}
                          {!item?.isPackageDelivery && opMode && OPERATION_MODE_CFG[opMode] && (
                            <div style={{ marginTop: 4 }}>
                              <StatusBadge
                                label={opMode}
                                cfg={OPERATION_MODE_CFG[opMode]}
                                className="lk-pill--xs"
                              />
                            </div>
                          )}
                        </td>
                        <td>
                          <div style={{ fontWeight: 600, color: "#1a2a3a", fontSize: "0.83rem" }}>
                            {item?.client_id?.name || item?.customerName || "—"}
                          </div>
                          <div className="pot-date">{item?.shippingAddress?.customerNumber}</div>
                        </td>
                        <td className="pot-td-right">
                          <div className="pot-amount">
                            {formatShs(item?.totalPrice || item?.itemsPrice)}
                          </div>
                          {/* The payment state sits under the amount (it used
                              to be a column); the method line is kept only
                              when the badge does not already say it. */}
                          <div className="pot-amount-pay">
                            <PaymentBadge order={item} />
                          </div>
                          {item?.paymentMethod &&
                            String(item.paymentMethod).toUpperCase() !== "COD" && (
                              <div className="pot-amount-sub">{item.paymentMethod}</div>
                            )}
                        </td>
                        <td>
                          {item?.ticketStage ? (
                            <TicketStageBadge stage={item.ticketStage} />
                          ) : (
                            <DeliveryStatusBadge status={delStatus} />
                          )}
                        </td>
                        {canRecover && (
                          <td className="pot-col-payment">
                            {needsRecovery(item) ? (
                              <button
                                type="button"
                                className="pot-view-btn pot-review-btn"
                                onClick={() => setRecoveryOrderId(item._id)}
                              >
                                <i className="fas fa-search-dollar" />
                                Review Payment
                              </button>
                            ) : (
                              <span className="pot-none">None</span>
                            )}
                          </td>
                        )}
                        <td className="pot-col-actions">
                          <button className="pot-view-btn pot-manage-btn" onClick={() => handleOrderManage(item)}>
                            <i className="fas fa-tasks" />
                            Manage Order
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
              {sortedOrders.length > 0 && (
                <tfoot>
                  <tr>
                    <td colSpan={4}>
                      {totalOrders != null ? `${Number(totalOrders).toLocaleString()} orders` : `${sortedOrders.length} orders`}
                      {activeFilterCount > 0 && " matching the filters"}
                      <span className="pot-foot-sub">{sortedOrders.length} on this page</span>
                    </td>
                    <td className="pot-td-right">
                      {formatShs(totalAmount != null ? totalAmount : pageAmount)}
                      <span className="pot-foot-sub">{formatShs(pageAmount)} on this page</span>
                    </td>
                    <td colSpan={canRecover ? 3 : 2} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          <div className="pot-filter-rail-wrap">
            <FilterPane
              open={paneOpen}
              onToggle={togglePane}
              groups={paneGroups}
              onRemove={removeFilterValue}
              onClearAll={clearFilters}
            >
              <div className="lk-filterpane-group">
                <div className="lk-filterpane-label">Placed between</div>
                <div className="pot-pane-dates">
                  <input
                    type="date"
                    aria-label="Placed from"
                    className={filters.from ? "is-set" : ""}
                    value={filters.from || ""}
                    max={filters.to || undefined}
                    onChange={(e) => setFilter("from", e.target.value)}
                  />
                  <input
                    type="date"
                    aria-label="Placed up to"
                    className={filters.to ? "is-set" : ""}
                    value={filters.to || ""}
                    min={filters.from || undefined}
                    onChange={(e) => setFilter("to", e.target.value)}
                  />
                </div>
              </div>
            </FilterPane>
          </div>
          </div>

          {/* ── Mobile Card List ── */}
          <div className="pot-card-list">
            {!sortedOrders.length ? (
              <div className="pot-empty">
                <div className="pot-empty-icon"><i className="fas fa-shopping-cart" /></div>
                <div className="pot-empty-msg">No orders found.</div>
              </div>
            ) : (
              sortedOrders.map((item, index) => {
                const opMode = vendorTypeFor(item);
                const delStatus = (item?.delivery_status || "PENDING").toUpperCase();
                return (
                  <div key={item?._id} className="pot-mobile-card">
                    <div className="pot-mc-row">
                      <div className="pot-mc-body">
                        <div className="pot-mc-num">
                          #{item?.momo_id?.order_number || item?._id?.slice(-8)}
                        </div>
                        <div className="pot-mc-vendor">
                          {item?.isPackageDelivery ? "Package Delivery" : vendorLabelFor(item)}
                        </div>
                        <div className="pot-mc-customer">
                          {item?.client_id?.name || item?.customerName || "—"}
                          {item?.shippingAddress?.customerNumber && ` · ${item.shippingAddress.customerNumber}`}
                        </div>
                        <div className="pot-mc-badges">
                          {item?.isPackageDelivery
                            ? <StatusBadge label="PACKAGE" cfg={{ bg: "#ede9fe", color: "#4c1d95", border: "#ddd6fe", icon: "fa-box" }} />
                            : opMode && OPERATION_MODE_CFG[opMode] && <StatusBadge
                                label={opMode}
                                cfg={OPERATION_MODE_CFG[opMode]}
                                className="lk-pill--xs"
                              />}
                          <StatusBadge label={sourceCfgFor(item).label} cfg={sourceCfgFor(item)} />
                          <PaymentBadge order={item} />
                          {item?.ticketStage ? (
                            <TicketStageBadge stage={item.ticketStage} />
                          ) : (
                            <DeliveryStatusBadge status={delStatus} />
                          )}
                        </div>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 5, alignItems: "flex-end" }}>
                        <button className="pot-view-btn pot-manage-btn" onClick={() => handleOrderManage(item)}>
                          <i className="fas fa-tasks" />
                          Manage Order
                        </button>
                        {canRecover && needsRecovery(item) && (
                          <button type="button" className="pot-view-btn pot-review-btn" onClick={() => setRecoveryOrderId(item._id)}>
                            <i className="fas fa-search-dollar" />
                            Review Payment
                          </button>
                        )}
                        <button
                          className="pot-copy-btn"
                          title="Copy Order ID"
                          onClick={() => copyOrderId(item)}
                        >
                          {copiedId === item._id
                            ? <><i className="fas fa-check" style={{ color: "#00b69b" }} /> Copied</>
                            : <><i className="fas fa-copy" /> ID</>
                          }
                        </button>
                      </div>
                    </div>
                    <div className="pot-mc-footer">
                      <span className="pot-mc-amount">
                        Shs {(item?.totalPrice || item?.itemsPrice || 0).toLocaleString()}
                      </span>
                      <span className="pot-mc-date">
                        {moment(item?.createdAt).format("DD MMM YYYY")}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>

        </div>

        {recoveryOrderId && <PaymentRecoveryDialog key={recoveryOrderId} orderId={recoveryOrderId} onClose={() => {
          setRecoveryOrderId(null); dispatch(getPaginatedOrders(page, selectedVendorId, filters));
        }} />}
        <PaginatedOrders page={page} />
      </div>
    </>
  );
};

export default PaginatedOrderTable;
